<template>
    <div :class="[classes.core, {'active': isOpen}]">
        <div :class="classes.wrapper">
            <input
                    type="text"
                    @input="onChange"
                    v-model="search"
                    :class="classes.input"
                    @focus="onFocus"
                    @keydown.down="onArrowDown"
                    @keydown.up="onArrowUp"
                    @keydown.enter.prevent="onEnter"
                    @keydown.esc="onEscape"
                    :placeholder="placeholder"
                    :disabled="disabled"
            />
            <span :class="classes.arrow" role="presentation" @click="onTriggerPresentation">
                <b role="presentation"></b>
            </span>
        </div>

        <div :class="classes.container"
             v-show="isOpen">
            <ul :class="classes.results"
                ref="results">
                <li :class="classes.load"
                    v-if="isLoading">
                    Loading results...
                </li>
                <li
                        v-else
                        v-for="(result, i) in results"
                        :key="i"
                        @click="setResult(result)"
                        :class="[{'is-active': i === pointer}, classes.result]">
                    {{ result.text }}
                    <template v-if="result.icon">
                        <i aria-hidden="true"
                           class="fa float-right"
                           :class="result.icon"></i>
                    </template>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    export default {
        name:      'autocomplete-dynamic-values',
        props:     {
            items:            {
                required: false,
                type:     Array
            },
            state:            {
                required: false,
                default:  false,
                type:     Boolean
            },
            value:            {
                required: false,
                default:  null,
            },
            placeholder:      {
                required: false,
                type:     String
            },
            disabled:         {
                required: false,
                default:  false,
                type:     Boolean
            },
            parent:           {
                required: false,
                default:  false,
                type:     Boolean
            },
            has_preset_value: {
                required: false,
                default:  false,
                type:     Boolean
            }
        },
        data() {
            return {
                isOpen:    false,
                isLoading: false,
                _value:    null,
                results:   [],
                search:    '',
                pointer:   -1,
                classes:   {
                    core:      'autocomplete',
                    wrapper:   'autocomplete-wrapper',
                    input:     'form-control',
                    arrow:     'autocomplete-arrow',
                    container: 'autocomplete-container',
                    results:   'autocomplete-results',
                    result:    'autocomplete-result',
                    load:      'autocomplete-loading'
                }
            }
        },
        methods:   {
            onChange() {
                const self = this;

                self._value = self.search || null;

                self.triggerInputEvent();

                // Is the data given by an outside ajax request?
                if (self.isAsync) {
                    self.isLoading = true;
                } else {
                    // Let's  our flat array
                    self.filterResults();

                    if (self.results.length > 0) {
                        self.isOpen = true;
                    } else {
                        self.isOpen = false;
                    }
                }
            },
            triggerInputEvent() {
                const self = this;

                // Let's warn the parent that a change was made
                self.$emit('input', self._value);
            },
            filterResults() {
                const self = this;

                // first uncapitalize all the things
                self.results = self.items.filter((item) => {
                    return item.text.toLowerCase().indexOf(self.search.toLowerCase()) > -1;
                });

            },
            setResult(result) {
                const self = this;

                self._value = result.id;
                self.isOpen = false;

                if (parseInt(result.id) === 0) {
                    self.search = '';
                } else {
                    self.search = result.text;
                }

                self.triggerInputEvent();
            },
            onArrowDown(event) {
                const self = this;

                // Disable when list isn't showing up
                if (!self.isOpen) return;

                event.preventDefault();

                if (self.pointer < (self.results.length - 1)) {
                    self.pointer += 1;

                    self.maybeAdjustScroll();
                }
            },
            onArrowUp(event) {
                const self = this;

                // Disable when list isn't showing up
                if (!self.isOpen) return;

                event.preventDefault();

                if (self.pointer > 0) {
                    self.pointer -= 1;

                    self.maybeAdjustScroll();
                }
            },
            onEnter() {
                const self = this;

                self.setResult(self.results[self.pointer]);
                self.pointer = -1;
            },
            onEscape() {
                const self = this;

                self.isOpen = false;
            },
            onFocus() {
                const self = this;

                if (self.results.length > 0) {
                    self.isOpen = true;
                } else {
                    self.isOpen = false;
                }
            },
            onTriggerPresentation() {
                const self = this;

                if (self.disabled) {
                    return false;
                }

                self.isOpen = !self.isOpen;
            },
            handleClickOutside(event) {
                const self = this;

                if (!self.$el.contains(event.target)) {
                    self.isOpen  = false;
                    self.pointer = -1;

                    self.setInitialValue();
                }
            },
            setInitialValue() {
                const self = this;

                const search = self.items.find((item) => {
                    return item.id === self.value;
                });

                if (search) {
                    self._value = search.id;

                    if (parseInt(search.id) === 0) {
                        self.search = '';
                    } else {
                        self.search = search.text;
                    }
                } else if (self.has_preset_value) {
                    self.search = self.value;
                }
            },

            /**
             * Adjust the scroll position of the dropdown list
             * if the current pointer is outside of the
             * overflow bounds.
             * @returns {*}
             */
            maybeAdjustScroll() {
                const self = this;

                let pixelsToPointerTop    = self.pixelsToPointerTop();
                let pixelsToPointerBottom = self.pixelsToPointerBottom();

                if (pixelsToPointerTop <= self.viewport().top) {
                    return self.scrollTo(pixelsToPointerTop);
                } else if (pixelsToPointerBottom >= self.viewport().bottom) {
                    return self.scrollTo(self.viewport().top + self.pointerHeight());
                }
            },

            /**
             * The distance in pixels from the top of the dropdown
             * list to the top of the current pointer element.
             * @returns {number}
             */
            pixelsToPointerTop() {
                const self = this;

                let pixelsToPointerTop = 0;
                let $results           = self.$refs.results;

                if (self.results.length === 0) {
                    return 0;
                }

                for (let i = 0; i < self.pointer; i++) {
                    pixelsToPointerTop += $results.children[i].offsetHeight;
                }

                return pixelsToPointerTop;
            },

            /**
             * The distance in pixels from the top of the dropdown
             * list to the bottom of the current pointer element.
             * @returns {*}
             */
            pixelsToPointerBottom() {
                const self = this;

                return self.pixelsToPointerTop() + self.pointerHeight();
            },

            /**
             * The offsetHeight of the current pointer element.
             * @returns {number}
             */
            pointerHeight() {
                const self = this;

                let $results = self.$refs.results;
                let element  = $results ? $results.children[self.pointer] : false;

                return element ? element.offsetHeight : 0
            },

            /**
             * The currently viewable portion of the options.
             * @returns {{top: (string|*|number), bottom: *}}
             */
            viewport() {
                const self = this;

                let $results = self.$refs.results;

                return {
                    top:    $results ? $results.scrollTop : 0,
                    bottom: $results ? $results.offsetHeight + $results.scrollTop : 0
                }
            },

            /**
             * Scroll the options to a given position.
             * @param position
             * @returns {*}
             */
            scrollTo(position) {
                const self = this;

                let $results = self.$refs.results;

                return $results ? $results.scrollTop = position : null;
            },
        },
        watch:     {
            items: function (value, oldValue) {
                const self = this;

                // actually compare them
                //if (value.length !== oldValue.length) {
                    self.results   = value;
                    self.isLoading = false;
                //}

                self.setInitialValue();
            },
            state: function (value) {
                const self = this;

                if (value === true) {
                    self.search = '';
                    self._value = '';

                    self.isOpen = false;
                }
            },
            value: function (value) {
                const self = this;

                self._value = value;

                self.setInitialValue();
            },
        },
        mounted() {
            const self = this;

            self._value = self.value;
            self.search = '';

            //self.filterResults();

            (self.parent ? jQuery(self.$el).closest('.modal-body')[0] : document)
                .addEventListener('click', self.handleClickOutside);
        },
        unmounted: function () {
            const self = this;

            (self.parent ? jQuery(self.$el).closest('.modal-body')[0] : document)
                .removeEventListener('click', self.handleClickOutside);
        }
    }
</script>